import { useGetSelectOptionByAppPartType, usePermissionV2 } from "@lhd/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppRecordV2,
  AppV2,
  RowDataTable,
  TableData,
  AppSelectionList,
  IPermissionItem,
} from "@lhd/types";
import { buildTreeAppPartType } from "../untils";
import { useParams } from "react-router-dom";
import { FormInstance } from "antd/es/form/hooks/useForm";
import { message } from "antd";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { APP_PART_TYPES } from "@lhd/features/Apps/AppForm/constants";
import { v4 as uuidv4 } from "uuid";
import DOMPurify from "dompurify";

export interface ReceivedProps {
  dataApp: AppV2;
  appRecord?: AppRecordV2;
  isEditPage?: boolean;
  isLoading?: boolean;
  isEditing?: boolean;
  isPDF?: boolean;
  form: FormInstance<any>;
  onChange?: () => void;
  allowSearchDate?: boolean;
  appType?: string;
  showPrintButton?: boolean;
  onPrintPDF?: () => void;
  isPrinting?: boolean;
  isCreate?: boolean;
  disabledInEditMode?: boolean;
  companyId?: number | string;
  selectionDataList?: AppSelectionList;
  isQuickViewMode?: boolean;
  permission?: IPermissionItem;
}

const useLayoutApp = ({
  dataApp,
  appRecord,
  isEditPage,
  isLoading,
  form,
  isEditing,
  onChange,
  isPDF,
  companyId,
  selectionDataList: selectionDataListProps,
  permission: permissionProps,
  ...restProps
}: ReceivedProps) => {
  const [recordData, setRecordData] = useState<AppRecordV2 | undefined>(
    appRecord,
  );

  useEffect(() => {
    if (appRecord != null) {
      setRecordData(appRecord);
    }
  }, [appRecord]);

  const appPartsFlat = useMemo(
    () => dataApp.app_parts.filter((x) => !!x.item_config_data) || [],
    [dataApp.app_parts],
  );

  const appParts = useMemo(
    () => buildTreeAppPartType(appPartsFlat),
    [appPartsFlat],
  );

  const { appId } = useParams();

  const fetchedPermission = usePermissionV2(dataApp);
  const permission = permissionProps || fetchedPermission;
  const fetchedSelectionDataList = useGetSelectOptionByAppPartType(
    appParts,
    companyId,
  );
  const selectionDataList = selectionDataListProps || fetchedSelectionDataList;

  const handleImportTableRows = useCallback(
    (tableItem: AppPartType, rows: string[][]) => {
      const columns = [...(tableItem.children || [])].sort(
        (a, b) => a.item_config_data.x - b.item_config_data.x,
      );

      if (!columns.length) return;

      if (!rows.length) {
        message.warning("インポートする行がありません");
        return;
      }

      const base = recordData ?? (appRecord || ({} as AppRecordV2));

      const newRows: RowDataTable[] = rows.map((row, rowIdx) => {
        const client_id = uuidv4();
        return {
          row_id: 0,
          row_order: rowIdx,
          client_id,
          cells: columns.map((col, colIdx) => ({
            id: 0,
            app_part_id: Number(col.id),
            value:
              col.app_part_type === APP_PART_TYPES.ATTACHMENT
                ? ""
                : DOMPurify.sanitize(String(row[colIdx] ?? "").trim()),
            background_color: null,
            text_color: null,
          })),
        };
      });
      const tableId = Number(tableItem.id);
      const table_data = base?.table_data
        ? [...base.table_data]
        : ([] as TableData[]);

      const ti = table_data.findIndex((t) => Number(t.table_id) === tableId);
      const existingTable = ti >= 0 ? table_data[ti] : undefined;

      /** Mark all previously persisted rows for deletion on save */
      const destroyPayload =
        existingTable?.rows
          ?.filter((r) => r.row_id > 0)
          .map((r) => ({ row_id: r.row_id, _destroy: true as const })) ?? [];

      const patch = {
        table_id: tableId,
        table_name: tableItem.label_text ?? "",
        field_code: tableItem.field_code ?? "",
        app_part_type: APP_PART_TYPES.TABLE,
        rows: newRows,
      };
      if (ti >= 0) {
        table_data[ti] = { ...table_data[ti], ...patch };
      } else {
        table_data.push(patch);
      }
      setRecordData({ ...base, table_data });

      const tablePathValue: (string | number)[] = [
        "tables",
        String(tableItem.id),
      ];
      /** Replace entire table form slice; only new client_ids + optional _destroy */
      const tableValue: Record<string, unknown> = {};

      newRows.forEach((r, index) => {
        const id = r.client_id;
        const value: Record<
          string,
          { value: string; text_color: null; background_color: null }
        > = {};
        columns.forEach((col, colIdx) => {
          value[String(col.id)] = {
            value: String(r.cells[colIdx]?.value ?? ""),
            text_color: null,
            background_color: null,
          };
        });
        tableValue[id] = {
          client_id: id,
          row_order: index,
          value,
        };
      });
      if (destroyPayload.length) {
        tableValue._destroy = destroyPayload;
      } else {
        const destroyPayload = form.getFieldValue([
          ...tablePathValue,
          "_destroy",
        ]);
        tableValue._destroy = destroyPayload || [];
      }

      form.setFieldValue(tablePathValue, tableValue);

      message.success("インポートしました");
      onChange?.();
    },
    [form, appRecord, recordData, onChange],
  );

  return {
    ...restProps,
    appRecord: recordData,
    isEditPage,
    isPDF,
    selectionDataList,
    appParts,
    appPartsFlat,
    form,
    isEditing,
    isLoading: isLoading,
    appId: appId ?? "",
    permission,
    onChange,
    handleImportTableRows,
  };
};

export default useLayoutApp;
